import { Link as LinkIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router-dom";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import { LinkedInLoginModal } from "common/components/LinkedInLogin";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import type { WorkspaceAccount } from "common/types";

import MinerAccountSelector from "./MinerAccountSelector";

import PlaceholderImage from "assets/images/empty-placeholders/error.png";
import ReconnectAccountImage from "assets/images/empty-placeholders/reconnect-account.png";

interface ReconnectAccountProps {
  account: WorkspaceAccount;
  isDrip: boolean;
}

export default function ReconnectAccount({
  account,
  isDrip,
}: ReconnectAccountProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { accounts_count, id: workspaceId } = useSelectedWorkspaceContext();

  const hasAccounts = accounts_count > 0;

  if (!hasAccounts) {
    return (
      <EmptyPlaceholder
        title="Connect a LinkedIn Account"
        subtitle="You need to have at least one connected LinkedIn account to create a campaign"
        imageSrc={ReconnectAccountImage}
        actionComponent={
          <Button asChild>
            <Link to={`/workspaces/${workspaceId}/accounts`}>
              Go to accounts
            </Link>
          </Button>
        }
      />
    );
  }

  const title = `Search account ${account ? "disconnected" : "removed"}`;

  if (isDrip) {
    return (
      <>
        <EmptyPlaceholder
          title={title}
          subtitle="Reconnect the selected search account to find targets for your campaign"
          imageSrc={PlaceholderImage}
          actionComponent={
            <Button
              onClick={() => setIsModalOpen(true)}
              leftIcon={<LinkIcon />}
              variant="secondary-black"
            >
              Reconnect
            </Button>
          }
        />

        <LinkedInLoginModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          account={account}
        />
      </>
    );
  }

  return (
    <EmptyPlaceholder
      title={title}
      subtitle={
        account
          ? "Reconnect your search account, or select a different one to find targets for your campaign"
          : "Select a search account to find targets for your campaign"
      }
      imageSrc={PlaceholderImage}
      actionComponent={
        <MinerAccountSelector className="w-[188px]" size="sm" align="center" />
      }
    />
  );
}
